import { takeLatest, put, call, all, select, delay } from 'redux-saga/effects';
import getBrowserHistory from 'yoda-core-components/lib/navigation/history/getBrowserHistory';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import map from 'lodash/map';
import FactorySaga from 'yoda-site-components/lib/sagas/FactorySaga';
import sessionActions from 'yoda-site-components/lib/actions/SessionAction';
import Cookies from 'yoda-core-components/lib/helpers/Cookies/Cookies';
import { UPDATE_APPLIED_GC_TO_APP } from 'yoda-site-components/lib/actionTypes/NativeAppActionTypes';
import { selectContext } from 'yoda-site-components/lib/selectors/ContextSelector';
import { getSubmittedOrderDetails } from 'yoda-interfaces/lib/Order/OrderApi';
import { getSkuDetails } from 'yoda-interfaces/lib/Catalog/CategoriesApi';
import showGlobalLoader from 'yoda-checkout-components/lib/actions/ShowGlobalLoaderAction';
import { getMessageContent } from 'yoda-checkout-components/lib/actions/MessageContentAction';
import {
    URL_CONFIRMATION_PAGE,
    URL_CART_PAGE,
    PLACE_ORDER_GENERIC_ERROR_MESSAGE,
    PLACE_ORDER_GENERIC_ERROR_TITLE,
    CURBSIDE_OCP_CONTENT,
    CHARGE_TYPE_SHIPTOHOME,
    CHARGE_TYPE_SHIPPING_AND_HANDLING,
    CHARGE_TYPE_SHIPTOSTORE,
} from 'yoda-checkout-components/lib/common/Constants';
import { doPostSSRLogs } from 'yoda-core-components/lib/helpers/Utils/Utils';
import { orderPlaced, updateCartCount } from 'yoda-site-components/lib/actions/NativeAppAction';
import { SET_ORDER_ERROR } from 'yoda-checkout-components/lib/actionTypes/ServiceErrorHandlerActionTypes';
import {
    getCurbsideEligible,
    getCartEventData,
} from 'yoda-checkout-components/lib/helpers/Utils/Utils';
import { updateCartUID } from 'yoda-checkout-components/lib/helpers/Utils/ATCCommonUtils';
import { LAST_ORDER_GET_REQUEST } from 'yoda-checkout-components/lib/actionTypes/PlaceOrderActionTypes';
import {
    setGetOrderApiRetryNum,
    setPlaceOrderInProgress,
} from 'yoda-checkout-components/lib/actions/PlaceOrderActions';
import { stringifyQuery } from 'yoda-core-components/lib/navigation/query/makeQuery';
import {
    LAST_ORDER_GET_REQUEST_SUCCESS,
    UPDATE_ITEM_COUNT,
    LAST_ORDER_NOT_FOUND,
} from '../actionTypes/OrderActionTypes';
import { POST_ORDER_DETAILS_TO_NATIVE } from '../actionTypes/NativeOrderConfirmationActionTypes';
import { SET_XSESSIONID, SET_ORDER_SOURCE } from '../actionTypes/CreateAccountPostOrderActionTypes';
import OrderDetailsModel from '../helpers/OrderDetailsModel';
import { getSkusFromDraftOrder } from '../helpers/Utils/Utils';
import { getOrderInfoInlineLoader } from '../actions/OrderInfoAction';
import { getOrderDetails } from '../actions/OrderConfirmationAction';
import ApplicationLogger from '../helpers/ApplicationLogger';

const logger = ApplicationLogger.getLogger();

function* handleGetOrderRetry(payload, alreadyTriedTimes) {
    yield all([put(setGetOrderApiRetryNum(alreadyTriedTimes + 1)), put(getOrderDetails(payload))]);
}

function* postOrderDetailsToNative() {
    const orderDetailsState = (state) => state;
    const orderObj = yield select(orderDetailsState);
    const eventCartData = getCartEventData(orderObj?.orderDetails?.data?.bag?.items);
    const filterCharges = orderObj?.orderCharges;
    const shippingCost = filterCharges.reduce((total, current) => {
        if (
            current.type === CHARGE_TYPE_SHIPPING_AND_HANDLING ||
            current.type === CHARGE_TYPE_SHIPTOSTORE ||
            current.type === CHARGE_TYPE_SHIPTOHOME
        ) {
            return total + current.amount;
        }
        return total;
    }, 0);
    const orderInfoPayload = {
        id: get(orderObj, 'orderInfo.orderId'),
        itemCount: get(orderObj, 'orderInfo.itemsCount'),
        total: get(orderObj, 'orderTotals.ORDER AMOUNT'),
        subTotal: get(orderObj, 'orderTotals.SUB TOTAL'),
        isBopisOrder: !isEmpty(get(orderObj, 'itemDeliverySummary.sameDayPickup')),
        discount: get(orderObj, 'orderTotals.ADJUSTMENT'),
        cartItems: eventCartData,
        shipping: shippingCost,
    };
    yield all([
        put(orderPlaced(orderInfoPayload)),
        // clears the cart counter
        put(updateCartCount(0)),
    ]);

    const giftCards = get(orderObj, 'orderDetails.data.billing.unnamed.giftCards', []);
    if (giftCards.length > 0) {
        const appliedGC = map(giftCards, ({ number, balance }) => ({
            giftCardNumber: number,
            balance,
        }));
        yield put({
            type: UPDATE_APPLIED_GC_TO_APP,
            payload: { data: appliedGC },
        });
        Cookies.remove('DP_APP_GC_BALANCE');
    }
}

function* handlePaypalTimeoutException(context, checkoutState, result, orderId) {
    let paypalTimeoutError = false;
    const retryNumber = ((context || {}).preference || {}).paypalTimeoutRetryNumber || 2;
    const alreadyTriedTimes = checkoutState.getOrderApiRetryNumber || 1;
    switch (
        result.response.status // NOSONAR
    ) {
        case 404:
            paypalTimeoutError = true;
            if (alreadyTriedTimes < retryNumber) {
                const payload = {
                    orderId,
                };
                const maxGetOrderRetryTime =
                    ((context || {}).featureFlags || {}).maxGetOrderRetryTime || 5000;
                yield delay(maxGetOrderRetryTime);
                yield call(handleGetOrderRetry, payload, alreadyTriedTimes);
            } else {
                yield put(showGlobalLoader(false));
                yield put(setPlaceOrderInProgress(false));
                yield put({
                    type: SET_ORDER_ERROR,
                    payload: {
                        placeOrder: {
                            errorCode: PLACE_ORDER_GENERIC_ERROR_TITLE,
                            errorMessage: PLACE_ORDER_GENERIC_ERROR_MESSAGE,
                            redirectUrl: URL_CART_PAGE,
                        },
                    },
                });
            }
            break;
        default:
            break;
    }

    return paypalTimeoutError;
}

function* orderConfirmationSaga(action) {
    const context = (state) => state.context;
    const checkoutPage = (state) => state.checkoutPage;
    const checkoutState = yield select(checkoutPage);
    const appContext = yield select(context);
    const isNative = (appContext && appContext.isNative) || false;
    const enableServerSideRendering = checkoutState.mountOnServer;
    if (__SERVER__ && !enableServerSideRendering) {
        return;
    }
    let loadOCPage = true;
    let cookies = {};
    if (__SERVER__) {
        // eslint-disable-next-line no-param-reassign
        action.headers = appContext.headers;
        cookies = action.headers ? action.headers.cookies : cookies;
    }
    try {
        yield put(getOrderInfoInlineLoader(true));
        const result = yield call(FactorySaga, getSubmittedOrderDetails, action);
        if (result.isSuccess) {
            const orderDetails = result.response;

            // To Add or Update UID Cookie
            !__SERVER__ && updateCartUID(orderDetails);
            if (!action.payload) {
                // eslint-disable-next-line no-param-reassign
                action.payload = {};
            }
            // eslint-disable-next-line no-param-reassign
            action.payload.skus = getSkusFromDraftOrder(orderDetails);
            // eslint-disable-next-line no-param-reassign
            action.payload.version = '/v2';
            const resultProductDetails = yield call(FactorySaga, getSkuDetails, action);
            if (resultProductDetails.isSuccess) {
                if (resultProductDetails.response && resultProductDetails.response.data) {
                    orderDetails.data.fitPredictorAttributesData =
                        resultProductDetails.response.data;
                }
            }
            // removing if condition because value should be populated on both client and server
            const emailSignature = result.response.headers.get('X-EMAIL-SIGNATURE');
            if (emailSignature) {
                orderDetails.data.headers = { emailSignature };
            }
            const orderSource = result.response.headers.get('X-Order-Source');
            const xSessionId = result.response.headers.get('X-Session-Id');
            const { applepay = true } = appContext?.featureFlags || {};
            if (applepay) {
                orderDetails.data.applePayEligible = true;
            }
            yield put({ type: LAST_ORDER_GET_REQUEST_SUCCESS, orderDetails });
            const orderDetailsModel = new OrderDetailsModel(orderDetails);
            yield all([
                put(orderDetailsModel.getOrderInfo('', appContext)),
                put(orderDetailsModel.getContactInfo()),
                put(orderDetailsModel.getOrderTotals()),
                put(orderDetailsModel.getBillingInfo()),
                put(orderDetailsModel.getShippingGroupInfo()),
                put(orderDetailsModel.getOrderItems()),
                put(orderDetailsModel.getGiftWrapInfo()),
                put(orderDetailsModel.getOrderAdjustments()),
                put(orderDetailsModel.getOrderCharges()),
                put(orderDetailsModel.getOrderPaymentInfo()),
                put(orderDetailsModel.getCertonaAttributes(appContext)),
                put(orderDetailsModel.getOrderGiftRegistries()),
                put(orderDetailsModel.getItemDeliverySummary()),
                put(getOrderInfoInlineLoader(false)),
                put(showGlobalLoader(false)),
                put(setPlaceOrderInProgress(false)),
                put({ type: UPDATE_ITEM_COUNT }),
                put({ type: SET_ORDER_SOURCE, orderSource }),
                put({ type: SET_XSESSIONID, xSessionId }),
            ]);
            const featureFlags = (appContext || {}).featureFlags || {};
            const { enableCurbsideBanner = true } = featureFlags;
            if (!__SERVER__ && enableCurbsideBanner) {
                const showCurbsideBanner = getCurbsideEligible(
                    result.response.data.bag.items || [],
                    'ocp'
                );
                if (showCurbsideBanner) {
                    const messageContentData = (state) => state.messageContentData;
                    const messageContent = yield select(messageContentData);
                    if (messageContent && !messageContent[CURBSIDE_OCP_CONTENT]) {
                        yield put(getMessageContent(CURBSIDE_OCP_CONTENT));
                    }
                }
            }
            if (isNative) {
                yield call(postOrderDetailsToNative);
            }
        } else if (!__SERVER__ && includes(window.location.pathname, URL_CONFIRMATION_PAGE)) {
            if (((result || {}).response || {}).status === 404) {
                yield put({ type: LAST_ORDER_NOT_FOUND });
                yield put(sessionActions.setSessionTimeOut(true));
                yield put(setPlaceOrderInProgress(false));
            } else {
                // to load fail case from here.
                const orderSource = result.response.headers.get('X-Order-Source');
                const xSessionId = result.response.headers.get('X-Session-Id');
                yield all([
                    put(getOrderInfoInlineLoader(false)),
                    put(showGlobalLoader(false)),
                    put(setPlaceOrderInProgress(false)),
                    put({ type: UPDATE_ITEM_COUNT }),
                    put({ type: SET_ORDER_SOURCE, orderSource }),
                    put({ type: SET_XSESSIONID, xSessionId }),
                ]);
            }
        } else if (result && result.response) {
            const hasPaypalErrors = yield call(
                handlePaypalTimeoutException,
                appContext,
                checkoutState,
                result,
                action.payload.orderId
            );
            loadOCPage = !hasPaypalErrors;
        }
        if (
            !__SERVER__ &&
            !includes(window.location.pathname, URL_CONFIRMATION_PAGE) &&
            loadOCPage
        ) {
            const ocURLConfig = {
                pathname: URL_CONFIRMATION_PAGE,
                search: stringifyQuery({
                    OrderId: action.payload.orderId,
                }),
            };
            getBrowserHistory().push(ocURLConfig);
        }
        const { cluster } = yield select(selectContext);
        doPostSSRLogs(
            logger,
            {
                requestInfo: `IS CONFIRMATION PAGE RENDERED ON SERVER SIDE : ${
                    enableServerSideRendering && result.isSuccess
                }`,
                response: result.response,
            },
            cookies,
            cluster
        );
        // to load fail case from here.
        const orderSource = result.response.headers.get('X-Order-Source');
        const xSessionId = result.response.headers.get('X-Session-Id');
        yield all([
            put(getOrderInfoInlineLoader(false)),
            put(showGlobalLoader(false)),
            put(setPlaceOrderInProgress(false)),
            put({ type: UPDATE_ITEM_COUNT }),
            put({ type: SET_ORDER_SOURCE, orderSource }),
            put({ type: SET_XSESSIONID, xSessionId }),
        ]);
    } catch (error) {
        yield all([
            put(getOrderInfoInlineLoader(false)),
            put(showGlobalLoader(false)),
            put(setPlaceOrderInProgress(false)),
        ]);
    }
}

const watchLastOrder = function* watchLastOrder() {
    yield takeLatest(LAST_ORDER_GET_REQUEST, orderConfirmationSaga);
    yield takeLatest(POST_ORDER_DETAILS_TO_NATIVE, postOrderDetailsToNative);
};
watchLastOrder.sagaName = 'watchLastOrder';

export default watchLastOrder;
